import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { MapPin, Truck, Warehouse, Edit, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { inventoryLocationsAPI } from '@shared/apiClient';

const typeIcon = (type) => {
  switch (type) {
    case 'warehouse':
      return Warehouse;
    case 'truck':
      return Truck;
    default:
      return MapPin;
  }
};

const typeLabel = (type) => {
  switch (type) {
    case 'warehouse':
      return 'Warehouse';
    case 'truck':
      return 'Truck';
    default:
      return 'Other';
  }
};

export default function InventoryLocations() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', notes: '', isActive: true });

  const { data, isLoading } = useQuery(
    ['inventoryLocations', showInactive],
    () => inventoryLocationsAPI.getLocations({ includeInactive: showInactive }),
    { keepPreviousData: true }
  );

  const updateMutation = useMutation(
    ({ id, data: payload }) => inventoryLocationsAPI.updateLocation(id, payload),
    {
      onSuccess: () => {
        toast.success('Location updated');
        queryClient.invalidateQueries('inventoryLocations');
        setEditingLocation(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update location');
      }
    }
  );

  const locations = data?.data?.locations || [];

  const openEdit = (location) => {
    setEditingLocation(location);
    setEditForm({
      name: location.name || '',
      notes: location.notes || '',
      isActive: location.isActive !== false
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!editingLocation) return;
    updateMutation.mutate({
      id: editingLocation.id,
      data: {
        name: editForm.name.trim(),
        notes: editForm.notes.trim() || null,
        isActive: editForm.isActive
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Locations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Main warehouse and technician truck stock locations ({locations.length} locations)
          </p>
        </div>
        <label className="inline-flex items-center text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
          />
          Show inactive
        </label>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading locations...</div>
        ) : locations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No inventory locations found.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locations.map((location) => {
                const Icon = typeIcon(location.type);
                const tech = location.technician;
                return (
                  <tr key={location.id} className={!location.isActive ? 'bg-gray-50 opacity-75' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{location.name}</div>
                          {location.isDefault && (
                            <div className="text-xs text-blue-600">Default warehouse</div>
                          )}
                          {location.notes && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">{location.notes}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {typeLabel(location.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {tech ? `${tech.firstName} ${tech.lastName}` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        location.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {location.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        type="button"
                        onClick={() => openEdit(location)}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium">About truck locations</p>
        <p className="mt-1 text-blue-800">
          A truck location is auto-created for each technician. Parts used on service calls deduct from the assigned technician&apos;s truck stock.
          Use Inventory item details to receive stock at the warehouse and transfer parts to trucks.
        </p>
      </div>

      {editingLocation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditingLocation(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Location</h3>
                <button type="button" onClick={() => setEditingLocation(null)} className="text-gray-400 hover:text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <label className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                  />
                  Active
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setEditingLocation(null)} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updateMutation.isLoading}>
                    {updateMutation.isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
